import { Button } from '@creditornot/cb-button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import noop from 'lodash/noop';
import * as R from 'ramda';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { updateVenueInventory } from 'apis/inventory-management-service/api';
import { bulkUpdatePickingAreas, getPickingAreas, updateItemConfiguration } from 'apis/picker-api/api';
import { ItemConfiguration } from 'apis/picker-api/types';
import { getBrandById, searchActiveBrands } from 'apis/product-information-catalog/api';
import {
  createItem,
  createItemBinding,
  getMenuConfigs,
  getProductInformationSettings,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { pickOptionBindingAttrs } from 'apis/restaurant-api/merchant-admin/menu2/utils';
import { VatCategory } from 'apis/vat-categories/types';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { initialVatFieldValue } from 'modules/menu/components/VatCategories';
import { getModifiedFieldsForTracking, useCreateItemTracking } from 'modules/menu/hooks/telemetryHooks';
import { useProcessMenuImage } from 'modules/menu/hooks/useProcessMenuImage';
import useMenuData from 'modules/menu/useMenuData';
import { flattenCategories, getParentCategory } from 'modules/menu/utils';

import StickyFooter from './Item/ItemDetails/StickyFooter';
import { FormValues, ItemDetailsFormFields, ValidationContext, validationSchema } from './ItemDetailsForm';
import { buildNewItemFormValues, cleanUpInvalidPrerequisiteOptionChoices } from './utils';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../constants';
import {
  transformApiFieldNamesToFormFieldNames,
  transformItemApiToFormValues,
  transformItemFormValuesToCreateApi,
  transformStorageLocationFormValueToApi,
} from '../transformers';

type Props = {
  initialCategoryId: string | null;
  itemToCopy?: Item;
  defaultVatCategoryCode: string | undefined;
  vatCategories: VatCategory[] | undefined;
  onSuccess: (itemId: string) => void;
  onClose: () => void;
  onItemConfigUpdated: (updatedConfigs: Record<string, ItemConfiguration>) => void;
};

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledItemDetailsFormFields = styled(ItemDetailsFormFields)`
  flex: 1 1 auto;
  overflow: auto;
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })`
  margin-inline-end: 16px;
`;

const SaveButton = styled(Button).attrs({ size: 'small', variant: 'blue' })``;

const AddOrCopyItem = ({
  initialCategoryId,
  itemToCopy,
  defaultVatCategoryCode,
  vatCategories,
  onSuccess,
  onClose,
  onItemConfigUpdated,
}: Props) => {
  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const currencyContext = useContext(CurrencyContext);
  const { venueId, menuId } = useContext(MenuMetaContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const menuFeatures = useContext(MenuFeaturesContext);

  const { categories, options, updateMenuData } = useMenuData();

  const processMenuImage = useProcessMenuImage();

  const { data: productInfoSettings } = useQuery(
    [`product-information-settings/${menuId}`],
    () => getProductInformationSettings(menuId),
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );

  const { data: menuConfigs } = useQuery(
    ['menu-configs', menuId, menuFeatures.hasCatalogSource],
    () => getMenuConfigs(menuId),
    {
      staleTime: Infinity,
      enabled: menuFeatures.hasCatalogSource || menuFeatures.hasReturnPoliciesEnabled,
    },
  );

  const { data: brand } = useQuery(
    ['product-brand', itemToCopy?.brand_id],
    () => (!itemToCopy?.brand_id ? Promise.resolve(null) : getBrandById(itemToCopy.brand_id)),
    { enabled: !!itemToCopy?.brand_id },
  );

  const { data: pickingAreas } = useQuery(['get-picking-areas', venueId], () => getPickingAreas(venueId), {
    enabled: menuFeatures.isRetail,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const pickingAreaItemAttrs = useMemo(() => {
    const allCategories = categories && flattenCategories(categories);
    const initialCategory = initialCategoryId
      ? allCategories?.find(category => category.id === initialCategoryId)
      : itemToCopy
        ? allCategories?.find(category => category.item_bindings.find(binding => binding.item_id === itemToCopy.id))
        : undefined;
    const initialParentCategory =
      (initialCategory && allCategories && getParentCategory(initialCategory.id, allCategories)) || initialCategory;
    const suggestedPickingArea = pickingAreas?.find(area =>
      initialParentCategory?.name.some(name => name.value === area.name),
    );
    if (!suggestedPickingArea) {
      return undefined;
    }
    return { pickingAreaId: suggestedPickingArea.id, pickingItemSequence: undefined };
  }, [categories, initialCategoryId, itemToCopy, pickingAreas]);

  const syncedFeedFields = useMemo(
    () => transformApiFieldNamesToFormFieldNames(menuConfigs?.source_config?.synced_field_names || []),
    [menuConfigs?.source_config?.synced_field_names],
  );

  const initialFormValues = useMemo(() => {
    if (itemToCopy) {
      return transformItemApiToFormValues(
        { ...itemToCopy, inventory_mode: null },
        {
          alcoholWarningPermilleLimit,
          productInformationSettings: productInfoSettings,
          language: primaryLanguage,
          categoryId: initialCategoryId,
          vatCategories,
          pickingAreaItemAttrs,
        },
      );
    }
    return buildNewItemFormValues(
      initialCategoryId,
      pickingAreaItemAttrs?.pickingAreaId,
      initialVatFieldValue(vatCategories, defaultVatCategoryCode),
      productInfoSettings?.nutrition_values,
    );
  }, [
    alcoholWarningPermilleLimit,
    initialCategoryId,
    itemToCopy,
    vatCategories,
    defaultVatCategoryCode,
    pickingAreaItemAttrs,
    primaryLanguage,
    productInfoSettings,
  ]);

  const initialNutritionFactsFormat = useMemo(() => {
    if (!itemToCopy) {
      return 'table';
    }
    return initialFormValues.nutritionValues.some(n => n.value !== undefined) ? 'table' : 'text';
  }, [initialFormValues.nutritionValues, itemToCopy]);

  const [nutritionFactsFormat, setNutritionFactsFormat] = useState<'text' | 'table'>(initialNutritionFactsFormat);

  const validationContext = useMemo<ValidationContext>(
    () => ({ currency: currencyContext.currency }),
    [currencyContext.currency],
  );
  const { trackAddItem, trackCopyItem, trackCreateSpecial } = useCreateItemTracking();

  const loadBrandOptions = useCallback(
    (search: string) =>
      searchActiveBrands(search).then(brands =>
        brands.map(brand => ({
          label: localize(brand.name.translations, brand.name.primaryLanguage),
          value: brand.id,
        })),
      ),
    [],
  );

  const { mutateAsync: submitItem } = useMutation(
    async ({ formValues, form }: { formValues: FormValues; form: FormApi<FormValues, Partial<FormValues>> }) => {
      const validValues = validationSchema.validateSync(formValues, { context: validationContext });
      const imageUrls = await Promise.all(
        validValues.image.map(async image => {
          return await processMenuImage(image);
        }),
      );
      const definedImageUrls = imageUrls.filter((url: string | null): url is string => url !== null);

      const haveOptionBindingsChanged = !R.equals(initialFormValues.optionBindings, validValues.optionBindings);
      const optionBindings = haveOptionBindingsChanged
        ? cleanUpInvalidPrerequisiteOptionChoices(validValues.optionBindings, initialFormValues.optionBindings, options)
        : validValues.optionBindings;

      const idStrippedOptionBindings = optionBindings.map(pickOptionBindingAttrs);

      const payload = transformItemFormValuesToCreateApi(validValues, idStrippedOptionBindings, definedImageUrls, {
        language: primaryLanguage,
        currency: currencyContext.currency,
        nutritionFactsFormat,
        menuId,
        externalInfoHyperlinkAllowed: menuFeatures.externalInfoHyperlinkAllowed,
      });

      const item = await createItem(menuId, payload);
      const isBinaryInventory = !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock';
      if (!isBinaryInventory && validValues.inventoryBalance !== undefined) {
        await updateVenueInventory(venueId, item.id, validValues.inventoryBalance);
      }
      const itemConfigUpdatePayload = {
        itemId: item.id,
        menuId,
        storageLocation: transformStorageLocationFormValueToApi(validValues),
      };

      const updatedItemConfig = await updateItemConfiguration(itemConfigUpdatePayload);
      onItemConfigUpdated({ [updatedItemConfig.itemId]: updatedItemConfig });
      updateMenuData({ actionType: 'addItem', payload: item });

      const modifiedFields = getModifiedFieldsForTracking<FormValues>(form.getState());
      const isWoltSpecial = initialFormValues.categoryId === WOLT_SPECIALS_CATEGORY_ID;

      if (isWoltSpecial) {
        trackCreateSpecial(modifiedFields);
      } else if (!!itemToCopy) {
        trackCopyItem(modifiedFields);
      } else {
        trackAddItem(item?.id ?? '', !!imageUrls.length, validValues.mandatoryWarnings, modifiedFields);
      }

      return item;
    },
    {
      onError: (error: any) => {
        toast.error(error.message, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: submitPickingAreas } = useMutation(
    async ({ formValues, item }: { formValues: FormValues; item: Item }) => {
      const pickingAreaName = pickingAreas?.find(area => area.id === formValues.pickingAreaId)?.name;
      if (!pickingAreaName) {
        return;
      }
      await bulkUpdatePickingAreas(venueId, [
        { itemId: item.id, pickingAreaName, order: formValues.pickingItemSequence || null },
      ]);
    },
    {
      onError: (error: any) => {
        toast.error(error.message, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: submitItemBinding } = useMutation(
    async ({ categoryId, itemId }: { categoryId: string; itemId: string }) =>
      createItemBinding(menuId, categoryId, { item_id: itemId }),
    {
      onSuccess: updatedMenu => {
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      },
    },
  );

  const handleSubmit = async (formValues: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
    const validValues = validationSchema.validateSync(formValues, { context: validationContext });

    const item = await submitItem({ formValues, form }).catch(noop);

    if (!item) {
      return;
    }

    if (formValues.pickingAreaId) {
      await submitPickingAreas({ formValues, item }).catch(noop);
    }

    if (validValues.categoryId !== null) {
      await submitItemBinding({ categoryId: validValues.categoryId, itemId: item.id }).catch(noop);
    }

    if (item.id) {
      onSuccess(item.id);
    }
  };

  return categories ? (
    <Form<FormValues>
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      validate={validateYupSchema(validationSchema, validationContext)}
    >
      {({ handleSubmit, hasValidationErrors, submitting, dirty }) => (
        <FormContainer onSubmit={handleSubmit}>
          <StyledItemDetailsFormFields
            allowCategoryChange={initialFormValues.categoryId !== WOLT_SPECIALS_CATEGORY_ID}
            allowUserRequirements={initialFormValues.categoryId === WOLT_SPECIALS_CATEGORY_ID}
            enableWoltPlusToggle={initialFormValues.categoryId === WOLT_SPECIALS_CATEGORY_ID}
            categories={categories}
            vatCategories={vatCategories}
            currency={currencyContext.currency}
            syncedFeedFields={syncedFeedFields}
            mode="create"
            onChangeNutritionFactsFormat={setNutritionFactsFormat}
            nutritionFactsFormat={nutritionFactsFormat}
            productInfoSettings={productInfoSettings}
            pickingAreas={pickingAreas}
            returnPolicies={menuConfigs?.item_return_policies}
            brand={brand ?? null}
            loadBrandOptions={loadBrandOptions}
          />
          <StickyFooter>
            <CancelButton variant="lightBlue" onClick={onClose} type="button">
              <FormattedMessage id="state.cancel" />
            </CancelButton>
            <SaveButton
              variant="blue"
              disabled={hasValidationErrors}
              loading={submitting}
              onClick={handleSubmit}
              type="submit"
            >
              <FormattedMessage id="state.save" />
            </SaveButton>
          </StickyFooter>
          <MenuEditorConfirmationPrompt when={dirty && !submitting} />
        </FormContainer>
      )}
    </Form>
  ) : null;
};

export default AddOrCopyItem;
